import { forwardRef, HTMLAttributes, PropsWithChildren } from "react";

const mergeClass = (...parts: (string | undefined)[]) =>
  parts.filter(Boolean).join(" ").trim();

export type CardProps = PropsWithChildren<
  HTMLAttributes<HTMLDivElement> & {
    /** Extra classes on the root `.card` */
    className?: string;
  }
>;

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, children, ...rest }, ref) => (
    <div ref={ref} className={mergeClass("card", className)} {...rest}>
      {children}
    </div>
  ),
);
Card.displayName = "Card";

export type CardHeaderProps = PropsWithChildren<
  HTMLAttributes<HTMLDivElement> & { className?: string }
>;

export const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, children, ...rest }, ref) => (
    <div ref={ref} className={mergeClass("card-header", className)} {...rest}>
      {children}
    </div>
  ),
);
CardHeader.displayName = "CardHeader";

export type CardBodyProps = PropsWithChildren<
  HTMLAttributes<HTMLDivElement> & { className?: string }
>;

export const CardBody = forwardRef<HTMLDivElement, CardBodyProps>(
  ({ className, children, ...rest }, ref) => (
    <div ref={ref} className={mergeClass("card-body", className)} {...rest}>
      {children}
    </div>
  ),
);
CardBody.displayName = "CardBody";

export type CardFooterProps = PropsWithChildren<
  HTMLAttributes<HTMLDivElement> & { className?: string }
>;

export const CardFooter = forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className, children, ...rest }, ref) => (
    <div ref={ref} className={mergeClass("card-footer", className)} {...rest}>
      {children}
    </div>
  ),
);
CardFooter.displayName = "CardFooter";

export type CardTitleProps = PropsWithChildren<
  HTMLAttributes<HTMLHeadingElement> & {
    className?: string;
    /** Default `h2` */
    as?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  }
>;

export const CardTitle = ({
  as: Tag = "h2",
  className,
  children,
  ...rest
}: CardTitleProps) => (
  <Tag className={mergeClass("card-title", className)} {...rest}>
    {children}
  </Tag>
);

export type CardSubtitleProps = PropsWithChildren<
  HTMLAttributes<HTMLParagraphElement> & { className?: string }
>;

export const CardSubtitle = ({
  className,
  children,
  ...rest
}: CardSubtitleProps) => (
  <p className={mergeClass("card-subtitle ", className)} {...rest}>
    {children}
  </p>
);

export type CardTextProps = PropsWithChildren<
  HTMLAttributes<HTMLParagraphElement> & { className?: string }
>;

export const CardText = ({ className, children, ...rest }: CardTextProps) => (
  <p className={mergeClass("card-text", className)} {...rest}>
    {children}
  </p>
);

export type CardImageProps = HTMLAttributes<HTMLImageElement> & {
  /** Bootstrap placement: top | bottom */
  placement?: "top" | "bottom";
  className?: string;
  alt: string;
};

export const CardImage = ({
  placement = "top",
  className,
  ...rest
}: CardImageProps) => (
  <img className={mergeClass(`card-img-${placement}`, className)} {...rest} />
);

export type CardImageOverlayProps = PropsWithChildren<
  HTMLAttributes<HTMLDivElement> & { className?: string }
>;

export const CardImageOverlay = ({
  className,
  children,
  ...rest
}: CardImageOverlayProps) => (
  <div className={mergeClass("card-img-overlay", className)} {...rest}>
    {children}
  </div>
);

export type CardLinkProps = PropsWithChildren<
  HTMLAttributes<HTMLAnchorElement> & {
    className?: string;
    href: string;
  }
>;

export const CardLink = ({ className, children, ...rest }: CardLinkProps) => (
  <a className={mergeClass("card-link", className)} {...rest}>
    {children}
  </a>
);
